import { Box, Menu, MenuItem, Tooltip } from "@mui/material";
import {
  RestaurantOutlined as MealIcon,
  MoreVert as MoreIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  ExpandLess,
  ExpandMore,
  StickyNote2Outlined as NoteIcon,
  BookmarkBorder as SaveMealIcon,
  ContentCopy as DuplicateIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { Card, EmptyState, InlineEdit, TextButton } from "../ui/primitives";
import { v2, nums } from "../ui/v2Tokens";
import { useAutoGrowTextarea } from "../ui/useAutoGrowTextarea";
import { MacroLine, TotalRow } from "../ui/MacroChips";
import MealTimeRange from "./MealTimeRange";
import QualityProteinLine from "./QualityProteinLine";
import NoteVisibility from "./NoteVisibility";
import FoodRow from "./FoodRow";
import FoodSearchField from "./FoodSearchField";
import AlternativeMealsSection, { AlternativeMealsControls } from "./AlternativeMealsSection";
import { mealTotals, type FoodItemV2, type MealV2 } from "../model/nutritionV2Model";

/** The small square controls in the meal header — reorder, options. Tappable on a phone. */
const ctrlBtn = (disabled = false) => ({
  width: { xs: "38px", md: "24px" },
  height: { xs: "34px", md: "22px" },
  borderRadius: "4px",
  border: "none",
  background: "transparent",
  color: disabled ? "#E3E8ED" : v2.ink4,
  cursor: disabled ? "default" : "pointer",
  display: "grid",
  placeItems: "center",
  padding: 0,
  fontFamily: "inherit",
  transition: "background .1s, color .1s",
  "&:hover": disabled ? {} : { background: v2.lineSoft, color: v2.ink },
});

type Props = {
  meal: MealV2;
  onChange: (meal: MealV2) => void;
  onDelete: () => void;
  /*
   * The three actions below only mean something for a meal that sits IN a menu, among others.
   * They are optional so this same card can edit a standalone meal — a company saved meal — where
   * there is nothing to reorder it against, nothing to duplicate it into, and where "save as a
   * saved meal" would offer to save it as itself.
   */
  onDuplicate?: () => void;
  onMove?: (direction: -1 | 1) => void;
  onSaveAsTemplate?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
};

const MealCard = ({
  meal,
  onChange,
  onDelete,
  onDuplicate,
  onMove,
  onSaveAsTemplate,
  canMoveUp,
  canMoveDown,
}: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  /**
   * Collapsed, a meal is ONE row: its name, what it comes to, and when it is eaten. The foods go,
   * and so do the two strips that used to survive folding — the totals row and the alternatives
   * section — because a "collapsed" meal that still stood three rows tall was not folded away in
   * any sense a trainer scanning a twelve-meal menu would recognise. Nothing is lost: the total
   * moves into the header beside the name, and the alternatives keep their count there.
   */
  const [collapsed, setCollapsed] = useState(false);
  // the note is opt-in: an empty textarea on every meal would be noise in a six-meal menu
  const [notesOpen, setNotesOpen] = useState(Boolean(meal.notes));
  const noteRef = useAutoGrowTextarea(meal.notes || "");
  // The alternatives' control rides on the meal's TOTAL row, so the state it drives lives here
  // rather than inside the section it opens.
  const [altsOpen, setAltsOpen] = useState(false);
  const [altPickerOpen, setAltPickerOpen] = useState(false);
  const totals = mealTotals(meal);

  const closeMenu = () => setAnchor(null);
  const run = (fn: () => void) => () => {
    closeMenu();
    fn();
  };

  // items carry an `order`, so any change to the list rewrites it to match the position
  const updateItems = (items: FoodItemV2[]) =>
    onChange({ ...meal, items: items.map((item, i) => ({ ...item, order: i })) });

  const moveItem = (uid: string, direction: -1 | 1) => {
    const index = meal.items.findIndex((it) => it.uid === uid);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= meal.items.length) return;
    const next = [...meal.items];
    [next[index], next[target]] = [next[target], next[index]];
    updateItems(next);
  };

  const altControls = (
    <AlternativeMealsControls
      count={(meal.alternatives || []).length}
      open={altsOpen}
      onToggle={() => setAltsOpen((v) => !v)}
      onAdd={() => setAltPickerOpen(true)}
    />
  );

  return (
    <Card data-testid="meal-card">
      {/* header — everything that identifies the meal, and everything that acts on it */}
      {/*
        On a phone this row wraps, and what it wraps INTO is the whole design: the name takes the
        space the macros used to, the collapse / reorder / options controls stay beside it because
        they are what the row is for, and the time range drops to a line of its own instead of being
        pushed to a far end that no longer exists at 375px.
      */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: "6px", md: "10px" },
          rowGap: { xs: "6px", md: "10px" },
          flexWrap: "wrap",
          padding: { xs: "10px 12px", md: "14px 16px" },
          borderBottom: collapsed ? "none" : `1px solid ${v2.lineSoft}`,
          transition: "background .12s",
          "&:hover": { background: v2.hover },
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label={collapsed ? `הרחב ${meal.name}` : `כווץ ${meal.name}`}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((v) => !v)}
          sx={{
            display: "grid",
            placeItems: "center",
            width: { xs: "40px", md: "26px" },
            height: { xs: "40px", md: "26px" },
            flex: "none",
            border: "none",
            background: "transparent",
            color: v2.ink3,
            cursor: "pointer",
            padding: 0,
            borderRadius: v2.rSm,
            "&:hover": { color: v2.ink },
          }}
        >
          {collapsed ? <ExpandMore sx={{ fontSize: 19 }} /> : <ExpandLess sx={{ fontSize: 19 }} />}
        </Box>

        <InlineEdit
          ariaLabel="שם הארוחה"
          value={meal.name}
          onChange={(name) => onChange({ ...meal, name })}
          // capped on desktop so it does not push the totals around; on a phone it takes the room
          // the macro figures gave up, because the name is what identifies the card
          // grows only on a phone; on desktop it sizes to its content up to 220px, as before
          sx={{ maxWidth: { xs: "none", md: "220px" }, flex: { xs: "1 1 auto", md: "0 1 auto" }, minWidth: 0 }}
        />

        {/* reordering sits beside the name, where the thing being moved is */}
        {onMove && (
        <Box
          sx={{
            display: "flex",
            // stacked, they are two 34px targets one above the other — 68px of a row that is
            // otherwise 40. Side by side on a phone they cost the row nothing.
            flexDirection: { xs: "row", md: "column" },
            gap: "1px",
            flex: "none",
          }}
        >
          <Tooltip title="העבר ארוחה למעלה" arrow placement="right">
            <Box component="span">
              <Box
                component="button"
                type="button"
                aria-label={`הזז למעלה ${meal.name}`}
                disabled={!canMoveUp}
                onClick={() => onMove(-1)}
                sx={ctrlBtn(!canMoveUp)}
              >
                <UpIcon sx={{ fontSize: 15 }} />
              </Box>
            </Box>
          </Tooltip>
          <Tooltip title="העבר ארוחה למטה" arrow placement="right">
            <Box component="span">
              <Box
                component="button"
                type="button"
                aria-label={`הזז למטה ${meal.name}`}
                disabled={!canMoveDown}
                onClick={() => onMove(1)}
                sx={ctrlBtn(!canMoveDown)}
              >
                <DownIcon sx={{ fontSize: 15 }} />
              </Box>
            </Box>
          </Tooltip>
        </Box>
        )}

        <Tooltip title="אפשרויות ארוחה" arrow placement="right">
          <Box
            component="button"
            type="button"
            aria-label={`אפשרויות ${meal.name}`}
            aria-expanded={Boolean(anchor)}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => setAnchor(e.currentTarget)}
            sx={{
              ...ctrlBtn(),
              width: { xs: "40px", md: "28px" },
              height: { xs: "40px", md: "28px" },
              borderRadius: "6px",
            }}
          >
            <MoreIcon sx={{ fontSize: 17 }} />
          </Box>
        </Tooltip>

        {/*
          Folded, the header carries the total itself — the row below that normally says it is not
          rendered. Smaller than TotalRow on purpose: this is a line to scan past, not the running
          answer a trainer builds against.
        */}
        {collapsed && meal.items.length > 0 && (
          <Box data-testid="meal-collapsed-totals" sx={{ minWidth: 0 }}>
            <MacroLine totals={totals} size={12} caloriesSize={13.5} />
          </Box>
        )}

        {/* and the alternatives keep their count, so folding never hides that a meal HAS swaps */}
        {collapsed && (meal.alternatives?.length || 0) > 0 && (
          <Box
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              whiteSpace: "nowrap",
              padding: "2px 8px",
              borderRadius: "20px",
              color: v2.ink2,
              background: v2.lineSoft,
              ...nums,
            }}
          >
            {/* same wording as the food row's own badge, singular included */}
            {meal.alternatives!.length === 1 ? "ארוחה חלופית אחת" : `${meal.alternatives!.length} ארוחות חלופיות`}
          </Box>
        )}

        {/*
          Pushed to the far end of the header — the space the macro figures used to occupy before
          they were removed as a duplicate of TotalRow. Left there, the time range sat crowded
          against the options button with the rest of the row empty beside it. `marginInlineStart`
          rather than `marginLeft` so it lands on the correct side under RTL.
        */}
        <Box
          sx={{
            // pushed to the far end of a wide header; on a phone there is no far end, so it takes
            // a line of its own rather than being squeezed against the options button
            marginInlineStart: { xs: 0, md: "auto" },
            width: { xs: "100%", md: "auto" },
          }}
        >
          <MealTimeRange
            mealName={meal.name}
            timeFrom={meal.timeFrom}
            timeTo={meal.timeTo}
            onChange={(changes) => onChange({ ...meal, ...changes })}
          />
        </Box>

        {/*
          The header used to repeat the meal's totals here. It no longer does: TotalRow says the
          same thing directly underneath, in larger type, and two copies of one number a few
          pixels apart is not redundancy a trainer benefits from — it is a second place to check
          when they disagree. TotalRow survives collapsing, so nothing is lost by folding a meal.

          "עדיין ריקה" stays, because it is the one thing TotalRow cannot say: it is not rendered
          at all for a meal with no foods, so without this an empty meal would show nothing.
        */}
        {meal.items.length === 0 && (
          <Box
            sx={{
              // no auto margin: the time range above now takes the free space, and two elements
              // both claiming it would split the row instead of pushing one end
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              fontSize: "12.5px",
              color: v2.ink3,
              ...nums,
            }}
          >
            עדיין ריקה
          </Box>
        )}

        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={closeMenu}
          PaperProps={{ sx: { borderRadius: v2.r, boxShadow: v2.shadowMenu, minWidth: "190px" } }}
        >
          {onSaveAsTemplate && (
            <MenuItem onClick={run(onSaveAsTemplate)} sx={{ fontSize: "13.5px", gap: "10px", color: v2.ink2 }}>
              <SaveMealIcon sx={{ fontSize: 16, color: v2.ink3 }} />
              שמור כארוחה שמורה
            </MenuItem>
          )}
          {onDuplicate && (
            <MenuItem onClick={run(onDuplicate)} sx={{ fontSize: "13.5px", gap: "10px", color: v2.ink2 }}>
              <DuplicateIcon sx={{ fontSize: 16, color: v2.ink3 }} />
              שכפל ארוחה
            </MenuItem>
          )}
          <MenuItem onClick={run(onDelete)} sx={{ fontSize: "13.5px", gap: "10px", color: v2.over }}>
            <DeleteIcon sx={{ fontSize: 16, color: v2.over }} />
            מחק ארוחה
          </MenuItem>
        </Menu>
      </Box>

      {/* collapsed keeps the header and the totals — enough to see the meal in the day */}
      {collapsed ? null : meal.items.length === 0 ? (
        <EmptyState compact icon={MealIcon} text="חפש מוצר מזון למטה כדי להוסיף פריטים לארוחה" />
      ) : (
        meal.items.map((item, i) => (
          <FoodRow
            key={item.uid}
            item={item}
            canMoveUp={i > 0}
            canMoveDown={i < meal.items.length - 1}
            onMove={(direction) => moveItem(item.uid, direction)}
            onChange={(next) => updateItems(meal.items.map((it) => (it.uid === item.uid ? next : it)))}
            onDelete={() => updateItems(meal.items.filter((it) => it.uid !== item.uid))}
          />
        ))
      )}

      {!collapsed && (
        <FoodSearchField onAdd={(item) => updateItems([...meal.items, { ...item, order: meal.items.length }])} />
      )}

      {/* folded away with everything else — the header keeps the count */}
      {!collapsed && (
        <AlternativeMealsSection
          mealName={meal.name}
          mainItems={meal.items}
          alternatives={meal.alternatives || []}
          onChange={(alternatives) => onChange({ ...meal, alternatives })}
          open={altsOpen}
          onOpenChange={setAltsOpen}
          pickerOpen={altPickerOpen}
          onPickerOpenChange={setAltPickerOpen}
        />
      )}

      {/*
        The total CLOSES the card, under everything that can change it — the foods, the search
        that adds more, and the alternatives. A total in the middle is one you have to re-find
        after every edit; at the foot it is always in the same place, and it is the last thing
        read before moving on to the next meal.

        It says "סה״כ ארוחה" and an alternative's own total says "סה״כ חלופה", which is what
        keeps this from reading as a total OF the alternatives now that it sits under them.
      */}
      {!collapsed && meal.items.length > 0 && <TotalRow totals={totals} action={altControls} />}
      {/* An empty meal has no total to hang it on, and must still be able to take an
          alternative — so the control gets a line of its own only while there is no total. */}
      {!collapsed && meal.items.length === 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "6px 14px",
            borderTop: `1px solid ${v2.lineSoft}`,
          }}
        >
          {altControls}
        </Box>
      )}
      {/* where this meal's protein actually comes from — see QualityProteinLine */}
      {!collapsed && meal.items.length > 0 && <QualityProteinLine totals={totals} />}

      {/*
        A note for this meal alone — "לאכול שעה לפני האימון", "אפשר להחליף את הפרי".

        Last, under the total. It is an instruction ABOUT the finished meal rather than part of
        building it, and sitting mid-card it split the foods from the figures they add up to.
      */}
      {!collapsed &&
        (notesOpen ? (
          <Box sx={{ padding: "12px 16px 14px", borderTop: `1px solid ${v2.lineSoft}`, background: "#FFFDF6" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
              <NoteIcon sx={{ fontSize: 14, color: "#8A6A17" }} />
              <Box sx={{ fontSize: "12px", fontWeight: 500, color: "#8A6A17" }}>הערה לארוחה</Box>
              {/* Opening a note has to be undoable. Clearing the text alone would leave an
                  empty box sitting under the meal for good, so this drops the text AND the
                  box — the same "הסר" the menu-level note has. */}
              <Box
                component="button"
                type="button"
                aria-label={`הסר הערה מ${meal.name}`}
                onClick={() => {
                  onChange({ ...meal, notes: "" });
                  setNotesOpen(false);
                }}
                sx={{
                  marginInlineStart: "auto",
                  border: "none",
                  background: "transparent",
                  color: v2.ink4,
                  fontSize: "12px",
                  fontFamily: "inherit",
                  padding: "3px 7px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  "&:hover": { background: "#F5EDD8", color: v2.over },
                }}
              >
                הסר
              </Box>
              {/* Folding is NOT removing, and the two have to be told apart at a glance: this
                  keeps the text and only puts the editor away. Without it the only way to get a
                  written note off the screen was to delete it. */}
              <Box
                component="button"
                type="button"
                aria-label={`כווץ את ההערה של ${meal.name}`}
                aria-expanded={true}
                onClick={() => setNotesOpen(false)}
                sx={{
                  display: "grid",
                  placeItems: "center",
                  width: { xs: "40px", md: "24px" },
                  height: { xs: "40px", md: "24px" },
                  border: "none",
                  background: "transparent",
                  color: v2.ink4,
                  borderRadius: "6px",
                  cursor: "pointer",
                  padding: 0,
                  "&:hover": { background: "#F5EDD8", color: "#8A6A17" },
                }}
              >
                <ExpandLess sx={{ fontSize: 17 }} />
              </Box>
            </Box>
            <Box
              component="textarea"
              ref={noteRef}
              aria-label={`הערה ל${meal.name}`}
              placeholder="לדוגמה: לאכול כשעה לפני האימון"
              rows={2}
              value={meal.notes || ""}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange({ ...meal, notes: e.target.value })}
              sx={{
                width: "100%",
                boxSizing: "border-box",
                // grows with the note rather than scrolling — see useAutoGrowTextarea
                resize: "none",
                overflow: "hidden",
                minHeight: "60px",
                padding: "9px 11px",
                borderRadius: v2.rSm,
                border: "1px solid #EADFC0",
                background: v2.surface,
                fontSize: "13.5px",
                fontFamily: "inherit",
                lineHeight: 1.6,
                color: v2.ink,
                outline: "none",
                "&::placeholder": { color: v2.ink4 },
                "&:focus": { borderColor: "#D9C88E", boxShadow: "0 0 0 3px rgba(217,200,142,.25)" },
              }}
            />
            <NoteVisibility
              visible={meal.notesVisible}
              onChange={(notesVisible) => onChange({ ...meal, notesVisible })}
              label="מוצגת למתאמן באפליקציה"
              ariaLabel={`הצג את ההערה של ${meal.name} למתאמן`}
            />
          </Box>
        ) : (
          <Box sx={{ padding: "8px 14px", borderTop: `1px solid ${v2.lineSoft}` }}>
            {/*
              A folded note must still announce itself. Once folding keeps the text, an empty
              "הוסף הערה" here would be a lie — the trainer wrote something and the card would
              read as though they had not. So a meal that HAS a note shows its first line and
              opens on click; only a meal with no note offers to add one.
            */}
            <TextButton
              quiet
              onClick={() => setNotesOpen(true)}
              sx={{
                height: "28px",
                padding: "0 8px",
                fontSize: "12.5px",
                color: meal.notes ? "#8A6A17" : v2.ink3,
                maxWidth: "100%",
              }}
            >
              <NoteIcon sx={{ fontSize: 14 }} />
              <Box
                component="span"
                sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {meal.notes ? meal.notes : "הוסף הערה לארוחה"}
              </Box>
              {meal.notes && <ExpandMore sx={{ fontSize: 15, flex: "none" }} />}
            </TextButton>
          </Box>
        ))}
    </Card>
  );
};

export default MealCard;
